import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Descriptions,
  Image,
  Input,
  InputNumber,
  Modal,
  message,
  Spin,
  Alert,
  Row,
  Col,
  Divider
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi, TryOnFeedbackDetail as FeedbackDetailType } from '../lib/api';

const { TextArea } = Input;

const TryOnFeedbackDetail: React.FC = () => {
  const navigate = useNavigate();
  const { feedbackId } = useParams<{ feedbackId: string }>();
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<FeedbackDetailType | null>(null);
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [refundAmount, setRefundAmount] = useState(20);
  const [reviewNotes, setReviewNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (feedbackId) {
      loadFeedbackDetails();
    }
  }, [feedbackId]);

  const loadFeedbackDetails = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getTryOnFeedbackDetails(feedbackId!);
      setFeedback(response);
      if (response.feedback.refund_amount) {
        setRefundAmount(response.feedback.refund_amount);
      }
    } catch (error: any) {
      message.error('Failed to load feedback: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!feedback) return;

    try {
      setProcessing(true);
      await adminApi.approveTryOnFeedback(feedback.feedback.id, {
        admin_id: 'admin',
        review_notes: reviewNotes,
        refund_amount: refundAmount
      });
      message.success('Feedback approved successfully');
      setApproveModalVisible(false);
      await loadFeedbackDetails();
    } catch (error: any) {
      message.error('Failed to approve: ' + (error.message || 'Unknown error'));
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!feedback || !reviewNotes.trim()) {
      message.warning('Review notes are required for rejection');
      return;
    }

    try {
      setProcessing(true);
      await adminApi.rejectTryOnFeedback(feedback.feedback.id, {
        admin_id: 'admin',
        review_notes: reviewNotes
      });
      message.success('Feedback rejected successfully');
      setRejectModalVisible(false);
      await loadFeedbackDetails();
    } catch (error: any) {
      message.error('Failed to reject: ' + (error.message || 'Unknown error'));
    } finally {
      setProcessing(false);
    }
  };

  const handleProcessRefund = async () => {
    if (!feedback) return;

    // Use native confirm dialog (simpler and more reliable)
    const confirmed = window.confirm(
      `Are you sure you want to process a refund of ${feedback.feedback.refund_amount || 20} ZAR to this user?`
    );

    if (!confirmed) return;

    try {
      setProcessing(true);
      const result = await adminApi.processTryOnRefund(feedback.feedback.id);
      message.success(`Refund processed: ${result.refund_amount} ZAR credited`);
      await loadFeedbackDetails();
    } catch (error: any) {
      message.error('Failed to process refund: ' + (error.message || 'Unknown error'));
    } finally {
      setProcessing(false);
    }
  };

  const getIssueLabels = (issues: string[]): string[] => {
    const labels: Record<string, string> = {
      clothes_not_applied: "Clothes don't apply properly",
      person_changed: "Person/model changed",
      poor_quality: "Poor image quality",
      missing_items: "Missing clothing items",
      wrong_position: "Items in wrong position",
      other: "Other issue"
    };

    return issues.map(issue => labels[issue] || issue);
  };

  if (loading || !feedback) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  const { feedback: feedbackData, user, model, clothes } = feedback;

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/feedback')}
          style={{ marginBottom: 16 }}
        >
          Back to Feedback List
        </Button>
        <h1 style={{ fontSize: 24, fontWeight: 600, margin: 0 }}>
          Feedback Details
        </h1>
      </div>

      {/* Beta Notice */}
      <Alert
        message="Beta Feature"
        description="This feature is in beta. Review feedback carefully and approve refunds only for legitimate issues."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* Feedback Info Card */}
      <Card title="Feedback Information" style={{ marginBottom: 16 }}>
        <Descriptions column={2}>
          <Descriptions.Item label="ID">
            <code>{feedbackData.id}</code>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={
              feedbackData.status === 'pending' ? 'orange' :
                feedbackData.status === 'approved' ? 'green' : 'red'
            }>
              {feedbackData.status.toUpperCase()}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Submitted">
            {new Date(feedbackData.created_at).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="User">
            <code>{user?.telegram_id || feedbackData.user_id}</code>
            {user?.full_name && <div style={{ fontSize: '12px', color: '#666' }}>{user.full_name}</div>}
          </Descriptions.Item>
          {feedbackData.reviewed_at && (
            <>
              <Descriptions.Item label="Reviewed">
                {new Date(feedbackData.reviewed_at).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Reviewed By">
                {feedbackData.reviewed_by || 'N/A'}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>

        {feedbackData.review_notes && (
          <>
            <Divider />
            <div>
              <strong>Review Notes:</strong>
              <div style={{ marginTop: 8, padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
                {feedbackData.review_notes}
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Try-On Images */}
      <Card title="Try-On Result" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          {model && (
            <Col span={12}>
              <div>
                <h4>Model Image</h4>
                <Image
                  src={model.presigned_url || model.image_url}
                  alt="Model"
                  style={{ maxWidth: '100%', borderRadius: '8px' }}
                />
                {model.generated_by === 'ai' && (
                  <Tag color="purple" style={{ marginTop: 8 }}>AI Generated Model</Tag>
                )}
              </div>
            </Col>
          )}
          {feedbackData.result_image_url && (
            <Col span={12}>
              <div>
                <h4>Try-On Result</h4>
                <Image
                  src={feedbackData.result_image_url}
                  alt="Try-on result"
                  style={{ maxWidth: '100%', borderRadius: '8px' }}
                />
              </div>
            </Col>
          )}
        </Row>
      </Card>

      {/* Clothing Items */}
      {clothes && clothes.length > 0 && (
        <Card title={`Clothing Items (${clothes.length})`} style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            {clothes.map((cloth) => (
              <Col span={6} key={cloth.id}>
                <Card
                  size="small"
                  cover={
                    cloth.image_url ? (
                      <Image
                        src={cloth.image_url}
                        alt={cloth.name}
                        style={{ height: 150, objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{ height: 150, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        No Image
                      </div>
                    )
                  }
                >
                  <Card.Meta
                    title={<div style={{ fontSize: '12px' }}>{cloth.name}</div>}
                    description={cloth.category && <Tag>{cloth.category}</Tag>}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* Issue Report */}
      <Card title="Issue Report" style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 16 }}>
          <h4>Issues Reported:</h4>
          <Space size={[8, 8]} wrap>
            {getIssueLabels(feedbackData.feedback_issues).map((label, index) => (
              <Tag key={index} color="red">{label}</Tag>
            ))}
          </Space>
        </div>

        {feedbackData.custom_message && (
          <div>
            <h4>Custom Message:</h4>
            <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '4px', whiteSpace: 'pre-wrap' }}>
              {feedbackData.custom_message}
            </div>
          </div>
        )}
      </Card>

      {/* Refund Status */}
      {feedbackData.status === 'approved' && (
        <Card title="Refund Status" style={{ marginBottom: 16 }}>
          {feedbackData.refunded ? (
            <div>
              <Tag color="green" style={{ fontSize: '16px', padding: '8px 16px' }}>
                ✓ Refunded: {feedbackData.refund_amount} ZAR
              </Tag>
              {feedbackData.refund_transaction_id && (
                <div style={{ marginTop: 12 }}>
                  <strong>Transaction ID:</strong> <code>{feedbackData.refund_transaction_id}</code>
                </div>
              )}
            </div>
          ) : (
            <div>
              <Alert
                message="Refund Pending"
                description={`This feedback has been approved for a ${feedbackData.refund_amount || 20} ZAR refund but has not been processed yet.`}
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Button
                type="primary"
                icon={<DollarOutlined />}
                onClick={handleProcessRefund}
                loading={processing}
                size="large"
              >
                Process Refund ({feedbackData.refund_amount || 20} ZAR)
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Actions */}
      {feedbackData.status === 'pending' && (
        <Card title="Review Actions">
          <Space size="large">
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => setApproveModalVisible(true)}
              size="large"
              style={{ background: '#52c41a', borderColor: '#52c41a' }}
            >
              Approve & Set Refund
            </Button>
            <Button
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => setRejectModalVisible(true)}
              size="large"
            >
              Reject
            </Button>
          </Space>
        </Card>
      )}

      {/* Approve Modal */}
      <Modal
        title="Approve Feedback"
        open={approveModalVisible}
        onOk={handleApprove}
        onCancel={() => setApproveModalVisible(false)}
        confirmLoading={processing}
        okText="Approve & Set Refund"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <label>Refund Amount (ZAR):</label>
            <InputNumber
              value={refundAmount}
              onChange={(value) => setRefundAmount(value || 20)}
              min={0}
              max={100}
              style={{ width: '100%', marginTop: 8 }}
            />
          </div>
          <div>
            <label>Review Notes (Optional):</label>
            <TextArea
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              rows={4}
              placeholder="Add notes about why this was approved..."
              style={{ marginTop: 8 }}
            />
          </div>
        </Space>
      </Modal>

      {/* Reject Modal */}
      <Modal
        title="Reject Feedback"
        open={rejectModalVisible}
        onOk={handleReject}
        onCancel={() => setRejectModalVisible(false)}
        confirmLoading={processing}
        okText="Reject"
        okButtonProps={{ danger: true }}
      >
        <div>
          <label>Review Notes (Required):</label>
          <TextArea
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            rows={4}
            placeholder="Explain why this feedback is being rejected..."
            style={{ marginTop: 8 }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default TryOnFeedbackDetail;
